// RankTrust Canonical Semantic Foundation — single source of truth for
// brand entity, category, and per-route SEO metadata.
//
// Design rules (Chief Architect, Feb 22 2026):
// - Organization + WebSite JSON-LD live STATICALLY in public/index.html so
//   crawlers see them in the initial HTML response.
// - Only per-route WebPage JSON-LD is emitted from React via <SEOHead>.
// - Stable @id values keep the entity graph consistent across pages.
// - No SearchAction (no public site-search exists).
// - No sameAs (no verified official social profiles as of Feb 2026).

export const SITE_ORIGIN = "https://ranktrust.io";

export const BRAND = Object.freeze({
  name: "RankTrust",
  legalName: "RankTrust",
  category: "Service Business Growth Platform",
  tagline: "Better Ranking. Better Workflow. Better Customer Experience.",
  logoUrl: `${SITE_ORIGIN}/ranktrust-logo.png`,
  defaultOgImage: `${SITE_ORIGIN}/ranktrust-logo.png`,
});

export const ENTITY_IDS = Object.freeze({
  organization: `${SITE_ORIGIN}/#organization`,
  website: `${SITE_ORIGIN}/#website`,
  webpage: (canonical) => `${canonical}#webpage`,
});

export function canonicalFor(path) {
  const clean = path.startsWith("/") ? path : `/${path}`;
  return clean === "/" ? `${SITE_ORIGIN}/` : `${SITE_ORIGIN}${clean}`;
}

export const ROBOTS = Object.freeze({
  INDEX: "index,follow",
  NOINDEX_FOLLOW: "noindex,follow",
  NOINDEX_NOFOLLOW: "noindex,nofollow",
});

export const ROUTE_META = Object.freeze({
  "/": {
    title: "RankTrust | Better Ranking. Better Workflow. Better Customer Experience.",
    description:
      "Discover hidden growth opportunities in your service business. RankTrust helps you identify what's holding you back, prioritize improvements, and turn more opportunities into customers.",
    ogTitle: "RankTrust | Better Ranking. Better Workflow. Better Customer Experience.",
    ogDescription:
      "Better Ranking. Better Workflow. Better Customer Experience. RankTrust helps service businesses get found, earn customer trust and improve the systems that support growth.",
    robots: ROBOTS.INDEX,
    indexable: true,
  },
  "/check": {
    title: "Free Google Maps, SEO & AI Visibility Assessment | RankTrust",
    description:
      "Check your Google Maps ranking, local SEO visibility, competitors, website health and AI search presence with a free RankTrust Business Growth Assessment.",
    ogTitle: "Free Business Growth Assessment | RankTrust",
    ogDescription:
      "See where your business stands across Google Maps, local SEO, competitors, website health and AI search visibility.",
    robots: ROBOTS.INDEX,
    indexable: true,
  },
  "/local-seo": {
    title: "Local SEO for Service Businesses | RankTrust",
    description: "Improve local search visibility, Google Business Profile relevance and local customer discovery with RankTrust local SEO for service businesses.",
    robots: ROBOTS.INDEX, indexable: true,
  },
  "/google-maps-ranking": {
    title: "Google Maps Ranking & 3-Pack Visibility | RankTrust",
    description: "See where your business ranks in Google Maps, compare competitors and improve the signals that influence local 3-pack visibility.",
    robots: ROBOTS.INDEX, indexable: true,
  },
  "/ai-search-visibility": {
    title: "AI Search Visibility & AEO for Service Businesses | RankTrust",
    description: "Improve how your business is understood and surfaced by ChatGPT, Perplexity and AI answer engines with AI search visibility and AEO.",
    robots: ROBOTS.INDEX, indexable: true,
  },
  "/website-optimization": {
    title: "Website Optimization for Service Businesses | RankTrust",
    description: "Improve website conversion, trust, mobile experience, SEO and AI answer readiness so more visitors become calls, leads and customers.",
    robots: ROBOTS.INDEX, indexable: true,
  },
  "/lead-follow-up": {
    title: "Lead Follow-Up & Speed-to-Lead Automation | RankTrust",
    description: "Respond faster, automate missed-call and lead follow-up, and reduce opportunities lost between first contact, estimate and booking.",
    robots: ROBOTS.INDEX, indexable: true,
  },
  "/workflow-automation": {
    title: "Workflow Automation for Service Businesses | RankTrust",
    description: "Automate repetitive admin, intake, reminders, scheduling and handoffs while connecting the systems your business already uses.",
    robots: ROBOTS.INDEX, indexable: true,
  },
  "/payments": {
    title: "Payments & Integration for Service Businesses | RankTrust",
    description: "Connect payment capabilities with your CRM, field-service, invoicing and customer workflows without replacing the systems that already work.",
    robots: ROBOTS.INDEX, indexable: true,
  },
  "/review-management": {
    title: "Review Management for Service Businesses | RankTrust",
    description: "Automate review requests, strengthen reputation signals and build a repeatable customer feedback process for service businesses.",
    robots: ROBOTS.INDEX, indexable: true,
  },
  "/pricing": {
    title: "Pricing | RankTrust — Business Growth Intelligence Platform",
    description:
      "Explore RankTrust Business Growth Intelligence plans for businesses and professionals who help businesses grow.",
    ogTitle: "RankTrust Pricing — Business Growth Intelligence Platform",
    ogDescription:
      "Start with opportunity discovery, prioritize what matters, and move into implementation when the right opportunity is clear.",
    robots: ROBOTS.INDEX,
    indexable: true,
  },
  "/demo": {
    title: "See a Sample Business Growth Assessment | RankTrust",
    description:
      "See how RankTrust surfaces business growth opportunities across visibility, competitive position, website intelligence and AI search presence.",
    ogTitle: "Sample Business Growth Assessment | RankTrust",
    ogDescription:
      "See how RankTrust turns business signals into opportunity discovery, evidence and prioritized next steps.",
    robots: ROBOTS.INDEX,
    indexable: true,
  },
  "/terms": {
    title: "Terms of Service | RankTrust",
    description:
      "RankTrust Terms of Service. The legal terms governing use of the RankTrust platform.",
    ogTitle: "Terms of Service | RankTrust",
    ogDescription: "The legal terms governing use of the RankTrust platform.",
    robots: ROBOTS.INDEX,
    indexable: true,
  },
  "/privacy": {
    title: "Privacy Policy | RankTrust",
    description:
      "RankTrust Privacy Policy. How RankTrust collects, uses, and protects your information.",
    ogTitle: "Privacy Policy | RankTrust",
    ogDescription:
      "How RankTrust collects, uses, and protects your information.",
    robots: ROBOTS.INDEX,
    indexable: true,
  },
  "/rankingalert": {
    title: "Ranking Alert | RankTrust",
    description:
      "Your RankTrust Ranking Alert — a personalized Business Growth Assessment prepared for your business.",
    robots: ROBOTS.NOINDEX_FOLLOW,
    indexable: false,
  },
  "/login": {
    title: "Sign In | RankTrust",
    description: "Sign in to your RankTrust account.",
    robots: ROBOTS.NOINDEX_FOLLOW,
    indexable: false,
  },
  "/register": {
    title: "Create Account | RankTrust",
    description: "Create a RankTrust account.",
    robots: ROBOTS.NOINDEX_FOLLOW,
    indexable: false,
  },
  "/onboarding": {
    title: "Onboarding | RankTrust",
    description: "RankTrust onboarding.",
    robots: ROBOTS.NOINDEX_NOFOLLOW,
    indexable: false,
  },
  "/billing/success": {
    title: "Billing | RankTrust",
    description: "RankTrust billing.",
    robots: ROBOTS.NOINDEX_NOFOLLOW,
    indexable: false,
  },
});

export function buildWebPageJsonLd({ canonical, title, description }) {
  return {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "@id": ENTITY_IDS.webpage(canonical),
    url: canonical,
    name: title,
    description,
    isPartOf: { "@id": ENTITY_IDS.website },
    about: { "@id": ENTITY_IDS.organization },
    publisher: { "@id": ENTITY_IDS.organization },
    inLanguage: "en-US",
  };
}
